import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
  TablePagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { format } from "date-fns";
import TransactionModal from "./TransactionModal";

const mockTransactions = [
  {
    id: "tx-1",
    type: "Compra",
    asset: "AVALAND",
    totalUSD: 1275,
    status: "Completada",
    date: new Date("2025-08-30T10:00:00Z"),
    amount: 50,
    pricePerToken: 25.5,
  },
  {
    id: "tx-2",
    type: "Depósito",
    asset: "USDT",
    totalUSD: 1000,
    status: "Pendiente",
    date: new Date("2025-08-28T15:30:00Z"),
    amount: 1000,
    walletAddress: "0x262626FaCadeBEEfC0DeCafeBabe1234567890AbC",
  },
  {
    id: "tx-3",
    type: "Compra",
    asset: "TERRALAND",
    totalUSD: 600,
    status: "Pendiente",
    date: new Date("2025-08-25T09:45:00Z"),
    amount: 20,
    pricePerToken: 30.0,
  },
  {
    id: "tx-4",
    type: "Retiro",
    asset: "USDT",
    totalUSD: 200,
    status: "Completada",
    date: new Date("2025-08-22T11:20:00Z"),
    amount: 200,
  },
  {
    id: "tx-5",
    type: "Compra",
    asset: "SOIL",
    totalUSD: 3500,
    status: "Completada",
    date: new Date("2025-08-21T18:00:00Z"),
    amount: 100,
    pricePerToken: 35.0,
  },
  {
    id: "tx-6",
    type: "Compra",
    asset: "AVALAND",
    totalUSD: 1500,
    status: "Completada",
    date: new Date("2025-08-19T09:00:00Z"),
    amount: 60,
    pricePerToken: 25.0,
  },
  {
    id: "tx-7",
    type: "Depósito",
    asset: "USDT",
    totalUSD: 500,
    status: "Completada",
    date: new Date("2025-08-18T12:00:00Z"),
    amount: 500,
  },
  {
    id: "tx-8",
    type: "Retiro",
    asset: "USDT",
    totalUSD: 100,
    status: "Pendiente",
    date: new Date("2025-08-15T16:00:00Z"),
    amount: 100,
  },
  {
    id: "tx-9",
    type: "Compra",
    asset: "TERRALAND",
    totalUSD: 900,
    status: "Completada",
    date: new Date("2025-08-14T11:00:00Z"),
    amount: 30,
    pricePerToken: 30.0,
  },
  {
    id: "tx-10",
    type: "Compra",
    asset: "SOIL",
    totalUSD: 7000,
    status: "Completada",
    date: new Date("2025-08-12T14:30:00Z"),
    amount: 200,
    pricePerToken: 35.0,
  },
];

const formatCurrency = (amount) => {
  return Number(amount).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const TransactionCard = ({ transaction, handleOpenModal }) => {
  let statusColor = "text.secondary";
  if (transaction.status === "Completada") {
    statusColor = "#33ab9f";
  } else if (transaction.status === "Pendiente") {
    statusColor = "#ffa726";
  }

  return (
    <Paper
      sx={{
        p: 2,
        backgroundColor: "rgba(255, 255, 255, 0.05)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        borderRadius: "10px",
        width: "90%",
        cursor: "pointer",
        transition: "background-color 0.3s ease, border-color 0.3s ease",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          borderColor: "rgba(255, 255, 255, 0.2)",
        },
      }}
      onClick={() => handleOpenModal(transaction)}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="body1" color="text.secondary">
            Fecha
          </Typography>
          <Typography variant="body2" color="text.primary">
            {format(transaction.date, "dd-MM-yyyy HH:mm")}
          </Typography>
        </Box>
        <Box textAlign="right">
          <Typography variant="body1" color="text.secondary">
            Valor Total
          </Typography>
          <Typography variant="body2" color="text.primary">
            {formatCurrency(transaction.totalUSD)}
          </Typography>
        </Box>
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt={1}
      >
        <Box>
          <Typography variant="body1" color="text.secondary">
            Tipo
          </Typography>
          <Typography variant="body2" color="text.primary">
            {transaction.type}
          </Typography>
        </Box>
        <Box textAlign="right">
          <Typography variant="body1" color="text.secondary">
            Estado
          </Typography>
          <Typography variant="body2" sx={{ color: statusColor }}>
            {transaction.status}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

const TransactionHistory = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [openModal, setOpenModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const handleOpenModal = (transaction) => {
    setSelectedTransaction(transaction);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedTransaction(null);
  };

  const paginatedTransactions = mockTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Stack direction="column" gap={3} mt={5}>
      <Typography variant="h4" color="text.secondary">
        Historial de Compras
      </Typography>

      {isMobile ? (
        <Stack spacing={2}>
          {paginatedTransactions.map((transaction) => (
            <TransactionCard
              key={transaction.id}
              transaction={transaction}
              handleOpenModal={handleOpenModal}
            />
          ))}
        </Stack>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "10px",
          }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="historial de transacciones">
            <TableHead sx={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}>
              <TableRow>
                {[
                  "Fecha",
                  "Tipo",
                  "Activo",
                  "Cantidad",
                  "Valor Total (USD)",
                  "Estado",
                ].map((header) => (
                  <TableCell key={header} align="center">
                    <Typography
                      variant="body1"
                      color="text.primary"
                      fontWeight="bold"
                    >
                      {header}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.data.map((transaction) => {
                let statusColor = "text.secondary";
                let statusText = "Pendiente";

                if (
                  [
                    "TRADE_COMPLETED",
                    "FIAT_DEPOSIT_RECEIVED",
                    "ON_CHAIN_INITIATED",
                  ].includes(transaction.status)
                ) {
                  statusText = "Procesado";
                }

                if (["CREATED"].includes(transaction.status)) {
                  statusColor = "#ffa726";
                }

                if (["ON_CHAIN_COMPLETED"].includes(transaction.status)) {
                  statusColor = "text.secondary";
                  statusText = "Completada";
                }
                if (["FAILED"].includes(transaction.status)) {
                  statusColor = "#ec0f0f";
                  statusText = "Fallido";
                }

                return (
                  <TableRow
                    key={transaction.id}
                    onClick={() => {
                      if (statusText === "Pendiente")
                        handleOpenModal(transaction);
                    }}
                    sx={{
                      cursor:
                        statusText === "Pendiente" ? "pointer" : "default",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.07)",
                      },
                    }}
                  >
                    <TableCell component="th" scope="row" align="center">
                      <Typography variant="subtitle1" color="text.secondary">
                        {format(transaction.createdAt, "dd-MM-yyyy HH:mm")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="subtitle1" color="text.secondary">
                        Compra
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="subtitle1" color="text.secondary">
                        {transaction.property.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="subtitle1" color="text.secondary">
                        {transaction.amountToken} {transaction.property.symbol}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="subtitle1" color="text.secondary">
                        {formatCurrency(transaction.toAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="subtitle1"
                        sx={{ color: statusColor }}
                      >
                        {statusText}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TablePagination
        component="div"
        count={mockTransactions.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
        sx={{
          color: "text.secondary",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "10px",
          backgroundColor: "rgba(255, 255, 255, 0.05)",
        }}
        rowsPerPageOptions={[5, 10, 25]}
      />

      <TransactionModal
        open={openModal}
        handleClose={handleCloseModal}
        transaction={selectedTransaction}
      />
    </Stack>
  );
};

export default TransactionHistory;
